import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { cn } from "../../utils/common/cn";
import { scopeLabel, type DisciplineRow } from "../../pages/AthenaPricingEngine/lib/api/discipline";

const pct = (value: number) => `${value.toFixed(2)}%`;
const signedPct = (value: number) => `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;

const helper = createColumnHelper<DisciplineRow>();

function groupReasons(reasons: string[]) {
  const counts = new Map<string, number>();
  for (const reason of reasons) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  return [...counts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
}

export function DisciplineTable({ rows }: { rows: DisciplineRow[] }) {
  const [selected, setSelected] = useState<DisciplineRow | null>(null);

  const columns = useMemo<ColumnDef<DisciplineRow, any>[]>(
    () => [
      helper.accessor((row) => scopeLabel(row.scope), {
        id: "scope",
        header: "Scope",
        cell: (info) => (
          <span className="font-medium text-foreground">{info.getValue() as string}</span>
        ),
      }),
      helper.accessor("targetYield", {
        header: "Target Yield",
        cell: (info) => pct(info.getValue()),
      }),
      helper.accessor("actualYield", {
        header: "Actual Yield",
        cell: (info) => pct(info.getValue()),
      }),
      helper.accessor("deviation", {
        header: "Deviation",
        cell: (info) => (
          <span
            className={cn(
              "font-semibold",
              info.getValue() < 0 ? "text-decline" : "text-foreground",
            )}
          >
            {signedPct(info.getValue())}
          </span>
        ),
      }),
      helper.accessor("actualInternalCommissionRate", {
        header: "Int Comm Rate (Actual Vs Target)",
        cell: (info) => (
          <span className="flex flex-col items-end leading-tight">
            <span className="tabular-nums">
              {info.getValue().toFixed(1)}% vs{" "}
              {info.row.original.targetInternalCommissionRate.toFixed(1)}%
            </span>
            <span
              className={cn(
                "text-[10px] tabular-nums",
                info.row.original.commissionRateDeviation > 0
                  ? "text-decline"
                  : "text-muted-foreground",
              )}
            >
              {info.row.original.commissionRateDeviation > 0 ? "+" : ""}
              {info.row.original.commissionRateDeviation.toFixed(1)} pts
            </span>
          </span>
        ),
      }),
      helper.accessor("overrideCount", { header: "Overrides", cell: (info) => info.getValue() }),
      helper.accessor("dealCount", { header: "Deals", cell: (info) => info.getValue() }),
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    initialState: { sorting: [{ id: "deviation", desc: false }] },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const reasonSummary = selected ? groupReasons(selected.overrideReasons) : [];

  return (
    <>
      <div className="overflow-x-auto border border-border bg-card">
        <Table className="min-w-[980px] border-collapse">
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow
                key={group.id}
                className="border-border bg-secondary/50 hover:bg-secondary/50"
              >
                {group.headers.map((header, i) => {
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        "h-auto cursor-pointer select-none px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground",
                        i === 0 ? "text-left" : "text-right",
                      )}
                    >
                      <span
                        className={cn(
                          "inline-flex items-center gap-1",
                          i === 0 ? "" : "justify-end",
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : sorted === "desc" ? (
                          <ArrowDown className="size-3" />
                        ) : null}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                tabIndex={0}
                onClick={() => setSelected(row.original)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelected(row.original);
                  }
                }}
                className="cursor-pointer border-border/60 hover:bg-secondary/40 focus:bg-secondary/40 focus:outline-none"
              >
                {row.getVisibleCells().map((cell, i) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "px-3 py-2.5 align-middle",
                      i === 0 ? "text-left" : "text-right tabular-nums",
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet open={selected != null} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">{scopeLabel(selected.scope)}</SheetTitle>
                <p className="text-xs text-muted-foreground">
                  {selected.overrideCount} overrides across {selected.dealCount} deals ·{" "}
                  deviation {signedPct(selected.deviation)}
                </p>
              </SheetHeader>

              <div className="mt-6 border border-border bg-card">
                <p className="border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Override reasons as captured
                </p>
                <ul className="divide-y divide-border/60">
                  {reasonSummary.map((item) => (
                    <li
                      key={item.reason}
                      className="flex items-baseline justify-between gap-4 px-4 py-3"
                    >
                      <span className="text-sm text-foreground">{item.reason}</span>
                      <span className="text-sm font-semibold tabular-nums text-muted-foreground">
                        {item.count}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
